import { useEffect, useState } from 'react';
import { projectsApi } from '@/api/projects.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useProjectList } from '@/modules/projects/shared/useProjectList';
import type { GanttView } from '@/types/projects';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProjectsGanttPage() {
  const { projects } = useProjectList();
  const [projectId, setProjectId] = useState<number | undefined>();
  const [gantt, setGantt] = useState<GanttView | null>(null);
  const [taskId, setTaskId] = useState('');
  const [dependsOnId, setDependsOnId] = useState('');

  useEffect(() => {
    if (!projectId) {
      setGantt(null);
      return;
    }
    projectsApi.ganttView(projectId).then(setGantt).catch((e) => showApiError(e, 'Failed to load Gantt'));
  }, [projectId]);

  const critical = new Set(gantt?.criticalPathTaskIds ?? []);

  const addDependency = async () => {
    if (!taskId || !dependsOnId) {
      notify.error('Enter both task IDs');
      return;
    }
    try {
      await projectsApi.addDependency({
        taskId: Number(taskId),
        dependsOnTaskId: Number(dependsOnId),
        dependencyType: 'FINISH_TO_START',
      });
      notify.success('Dependency added');
      if (projectId) setGantt(await projectsApi.ganttView(projectId));
    } catch (err) {
      showApiError(err, 'Add dependency failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Gantt"
        description="Timeline view with dependencies and critical path"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Projects', to: '/projects' },
          { label: 'Gantt' },
        ]}
      />
      <Select
        className="mb-4"
        placeholder="Select project"
        value={String(projectId ?? '')}
        onChange={(v) => setProjectId(v ? Number(v) : undefined)}
        options={projects.map((p) => ({ value: String(p.id), label: p.projectName }))}
      />

      <div className="card mb-4 grid gap-2 p-4 md:grid-cols-3 items-end">
        <Input label="Task ID" value={taskId} onChange={(e) => setTaskId(e.target.value)} />
        <Input label="Depends on task ID" value={dependsOnId} onChange={(e) => setDependsOnId(e.target.value)} />
        <Button onClick={() => void addDependency()} disabled={!projectId}>
          Add dependency
        </Button>
      </div>

      <div className="card p-4 space-y-3">
        {(gantt?.tasks ?? []).map((t) => {
          const start = t.startDate ? new Date(t.startDate).getTime() : 0;
          const end = t.dueDate ? new Date(t.dueDate).getTime() : start;
          const span = Math.max(end - start, 86400000);
          const widthPct = Math.min(100, Math.max(8, span / 86400000));
          return (
            <div
              key={t.taskId}
              className={`rounded border p-3 ${critical.has(t.taskId) ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20' : ''}`}
            >
              <div className="flex justify-between text-sm">
                <span className="font-medium">{t.title}</span>
                <span className="text-slate-500">
                  {t.startDate ?? '—'} → {t.dueDate ?? '—'}
                </span>
              </div>
              <div className="mt-2 h-2 rounded bg-slate-100 dark:bg-slate-800">
                <div className="h-2 rounded bg-primary-500" style={{ width: `${widthPct}%` }} />
              </div>
              {t.dependsOnTaskIds.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">Depends on: {t.dependsOnTaskIds.join(', ')}</p>
              )}
              {critical.has(t.taskId) && <span className="text-xs text-amber-700">Critical path</span>}
            </div>
          );
        })}
        {!gantt?.tasks.length && <p className="text-sm text-slate-500">No tasks with dates for this project.</p>}
      </div>
    </div>
  );
}
